import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Loader2, Plus, Pencil, KeyRound, ShieldCheck, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const ROLES = ["ORG_ADMIN", "PORTFOLIO_LEAD", "MEMBER", "ACCOUNTS", "PROCUREMENT", "STORES", "HR"];

const PROJECT_ROLES = ["PROJECT_MANAGER", "ENGINEERING", "PROCUREMENT", "SITE", "VIEWER"];
const SEES_ALL_ROLES = ["ORG_ADMIN", "PORTFOLIO_LEAD"];

const EMPTY_FORM = { full_name: "", email: "", password: "", role: "MEMBER", phone: "", all_projects: false, designation_id: "" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [access, setAccess] = useState({}); // project_id -> project role
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [designations, setDesignations] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, projRes, empRes, desRes] = await Promise.all([
        api.get("/users"), api.get("/projects").catch(() => ({ data: [] })), api.get("/payroll/employees").catch(() => ({ data: [] })),
        api.get("/masters/designations").catch(() => ({ data: [] })),
      ]);
      setDesignations(desRes.data || []);
      setUsers(data);
      setProjects(projRes.data || []);
      setEmployees((empRes.data || []).filter((e) => e.is_active !== false));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setEmployeeId("");
    setForm(EMPTY_FORM);
    setAccess({});
    setOpen(true);
  };

  const openEdit = async (u) => {
    setEditing(u);
    setForm({ full_name: u.full_name, email: u.email, password: "", role: u.role, phone: u.phone || "", all_projects: Boolean(u.all_projects), designation_id: u.designation_id || "" });
    setAccess({});
    setOpen(true);
    setLoadingAccess(true);
    try {
      const { data } = await api.get(`/users/${u.id}/projects`);
      setAccess(Object.fromEntries((data || []).map((m) => [m.project_id, m.role])));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingAccess(false);
    }
  };

  const projectAccessPayload = () => Object.entries(access).map(([project_id, role]) => ({ project_id, role }));
  const toggleProject = (id, on) => {
    const next = { ...access };
    if (on) next[id] = next[id] || designationProjectRole;
    else delete next[id];
    setAccess(next);
  };
  const seesAllByRole = SEES_ALL_ROLES.includes(form.role);
  const chosenDesignation = designations.find((d) => d.id === form.designation_id);
  const designationProjectRole = chosenDesignation?.project_role || "VIEWER";
  // Picking a title fills the organisation role (never overriding an Org admin) and, for a new user,
  // the role on projects already ticked. Everything stays editable afterwards.
  const pickDesignation = (id) => {
    const d = designations.find((x) => x.id === id);
    if (!d) { setForm({ ...form, designation_id: "" }); return; }
    setForm({ ...form, designation_id: id, role: form.role === "ORG_ADMIN" ? form.role : d.org_role });
    if (!editing) setAccess(Object.fromEntries(Object.keys(access).map((pid) => [pid, d.project_role])));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          full_name: form.full_name, role: form.role, phone: form.phone || null,
          all_projects: form.all_projects, project_access: projectAccessPayload(), designation_id: form.designation_id || null,
        };
        if (form.password) payload.password = form.password;
        const { data } = await api.patch(`/users/${editing.id}`, payload);
        setUsers((prev) => prev.map((u) => (u.id === data.id ? data : u)));
        toast.success("User updated");
      } else {
        const { data } = await api.post("/users", {
          full_name: form.full_name, email: form.email, password: form.password, role: form.role, phone: form.phone || null,
          all_projects: form.all_projects, project_access: projectAccessPayload(), designation_id: form.designation_id || null,
        });
        setUsers((prev) => [...prev, data].sort((a, b) => a.full_name.localeCompare(b.full_name)));
        toast.success("User created — they can log in with the email and password you set");
      }
      setOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u, is_active) => {
    setTogglingId(u.id);
    try {
      const { data } = await api.patch(`/users/${u.id}`, { is_active });
      setUsers((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <PageHeader
        title="Users"
        description="Create logins and assign a role — module access follows the Permission Matrix for that role."
        action={<Button onClick={openCreate} data-testid="add-user-button"><Plus className="h-4 w-4" /> Add User</Button>}
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="rounded-xl border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Designation</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Projects</TableHead>
                <TableHead className="text-center">Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium text-foreground">{u.full_name}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell className="text-muted-foreground" data-testid={`user-designation-${u.email}`}>{u.designation || "—"}</TableCell>
                  <TableCell><Badge variant="secondary" data-testid={`user-role-badge-${u.email}`}>{u.role.replaceAll("_", " ")}</Badge></TableCell>
                  <TableCell className="text-muted-foreground">{u.phone || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground" data-testid={`user-projects-${u.email}`}>
                    {SEES_ALL_ROLES.includes(u.role) ? "All (by role)" : u.all_projects ? "All projects" : "Selected projects"}
                  </TableCell>
                  <TableCell className="text-center">
                    {togglingId === u.id ? (
                      <Loader2 className="mx-auto h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Switch
                        checked={u.is_active}
                        disabled={u.id === me?.id}
                        onCheckedChange={(v) => toggleActive(u, v)}
                        data-testid={`user-active-switch-${u.email}`}
                      />
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button asChild variant="ghost" size="sm" title="Projects, what they can do, and why they can't" data-testid={`user-page-button-${u.email}`}>
                      <Link to={`/erp/governance/users/${u.id}`}><Eye className="h-3.5 w-3.5" /></Link>
                    </Button>
                    {u.role !== "ORG_ADMIN" && (
                      <Button asChild variant="ghost" size="sm" title="Manage access: View, Create, Edit and Delete for every feature" data-testid={`access-user-button-${u.email}`}>
                        <Link to={`/erp/governance/access?user=${u.id}`}><ShieldCheck className="h-3.5 w-3.5" /></Link>
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-button-${u.email}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground">No users yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit User" : "Add User"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editing && employees.length > 0 && (
              <div>
                <Label>Employee <span className="font-normal text-muted-foreground">(fills name, email and phone)</span></Label>
                <Select
                  value={employeeId || "none"}
                  onValueChange={(v) => {
                    const emp = employees.find((e) => e.id === v);
                    setEmployeeId(v === "none" ? "" : v);
                    if (emp) {
                      setForm({ ...form, full_name: emp.name, email: emp.email || form.email, phone: emp.phone || form.phone });
                      if (emp.project_id && !access[emp.project_id]) setAccess({ ...access, [emp.project_id]: "SITE" });
                    }
                  }}
                >
                  <SelectTrigger className="mt-1.5" data-testid="user-employee-select"><SelectValue placeholder="Pick from the employee list" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not an employee / type details</SelectItem>
                    {employees
                      .filter((e) => !users.some((u) => e.email && u.email?.toLowerCase() === e.email.toLowerCase()))
                      .map((e) => <SelectItem key={e.id} value={e.id}>{e.code} — {e.name}{e.designation ? ` (${e.designation})` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                {employeeId && !employees.find((e) => e.id === employeeId)?.email && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">This employee has no email — type one below (it is the login ID).</p>
                )}
              </div>
            )}
            <div>
              <Label>Full name</Label>
              <Input className="mt-1.5" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-full-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                className="mt-1.5"
                value={form.email}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> {editing ? "New password (leave blank to keep current)" : "Password"}</Label>
              <Input
                type="password"
                className="mt-1.5"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? "••••••••" : "Minimum 8 characters"}
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label>Designation</Label>
              <Select value={form.designation_id || "none"} onValueChange={(v) => pickDesignation(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="user-designation-select"><SelectValue placeholder="Pick a designation" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No designation</SelectItem>
                  {designations.filter((d) => d.is_active || d.id === form.designation_id).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {chosenDesignation && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Starts as {chosenDesignation.org_role.replaceAll("_", " ")} · {chosenDesignation.project_role.replaceAll("_", " ")} on projects. Change either below if this person needs different access.
                </p>
              )}
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
              <p className="mt-1.5 text-xs text-muted-foreground">Module access for this role is controlled in the Permission Matrix.</p>
            </div>
            <div>
              <Label>Phone (optional)</Label>
              <Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-phone-input" />
            </div>

            <div className="rounded-lg border border-border p-3" data-testid="user-project-access">
              <p className="text-sm font-medium text-foreground">Project access</p>
              {seesAllByRole ? (
                <p className="mt-1 text-xs text-muted-foreground">{form.role.replaceAll("_", " ")} sees every project automatically.</p>
              ) : (
                <>
                  <div className="mt-2 flex items-center gap-2">
                    <Switch checked={form.all_projects} onCheckedChange={(v) => setForm({ ...form, all_projects: v })} data-testid="user-all-projects-switch" />
                    <Label>All projects — current and future</Label>
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Good for Accounts / Stores staff who work across every site. They see all projects' documents;
                    inside a project they act as a Viewer unless you give them a role below.
                  </p>
                  <p className="mt-3 text-xs font-medium text-foreground">
                    {form.all_projects ? "Project roles (optional — for extra rights on specific projects)" : "Projects this user works on"}
                  </p>
                  {loadingAccess ? (
                    <p className="mt-1 text-xs text-muted-foreground">Loading…</p>
                  ) : projects.length === 0 ? (
                    <p className="mt-1 text-xs text-muted-foreground">No projects yet.</p>
                  ) : (
                    <div className="mt-1.5 max-h-56 space-y-1.5 overflow-y-auto pr-1">
                      {projects.map((p) => {
                        const on = access[p.id] !== undefined;
                        return (
                          <div key={p.id} className="flex items-center gap-2" data-testid={`user-project-row-${p.code}`}>
                            <input type="checkbox" checked={on} onChange={(e) => toggleProject(p.id, e.target.checked)} className="h-4 w-4" />
                            <span className="min-w-0 flex-1 truncate text-sm text-foreground">{p.code} — {p.name}</span>
                            <Select value={access[p.id] || "VIEWER"} onValueChange={(v) => setAccess({ ...access, [p.id]: v })} disabled={!on}>
                              <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>{PROJECT_ROLES.map((r) => <SelectItem key={r} value={r}>{r.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {!form.all_projects && Object.keys(access).length === 0 && (
                    <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-400">No projects selected — this user won't see any project.</p>
                  )}
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="user-form-cancel-button">Cancel</Button>
            <Button
              onClick={save}
              disabled={saving || !form.full_name || !form.email || (!editing && !form.password)}
              data-testid="user-form-save-button"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
